/** The developer-visible error log (SPEC "Error Handling") plus undo/redo dispatch. */

import { MAX_UI_ERRORS } from '../types';
import type { ActionContext } from './context';

export interface ErrorActions {
  pushError(message: string): void;
  clearErrors(): void;
  dismissToast(): void;
  /** True while a persistence write is queued or in flight (see `UiState.saving`). */
  setSaving(saving: boolean): void;
  /** True while another tab is holding the database open against an upgrade or a delete. */
  setDbBlocked(blocked: boolean): void;
}

export interface HistoryActions {
  undo(): void;
  redo(): void;
  /** Drop the whole history (used after loading a different document). */
  clearHistory(): void;
}

export function createErrorActions(ctx: ActionContext): ErrorActions {
  return {
    pushError(message): void {
      const ui = ctx.getState().ui;
      const errors = [...ui.errors, message];
      if (errors.length > MAX_UI_ERRORS) errors.splice(0, errors.length - MAX_UI_ERRORS);
      ctx.set({ ui: { ...ui, errors } });
    },

    clearErrors(): void {
      const ui = ctx.getState().ui;
      if (ui.errors.length === 0) return;
      ctx.set({ ui: { ...ui, errors: [] } });
    },

    dismissToast(): void {
      const ui = ctx.getState().ui;
      if (!ui.toast) return;
      ctx.set({ ui: { ...ui, toast: null } });
    },

    setSaving(saving): void {
      const ui = ctx.getState().ui;
      if (ui.saving === saving) return;
      ctx.set({ ui: { ...ui, saving } });
    },

    setDbBlocked(blocked): void {
      const ui = ctx.getState().ui;
      if (ui.dbBlocked === blocked) return;
      ctx.set({ ui: { ...ui, dbBlocked: blocked } });
    },
  };
}

export function createHistoryActions(ctx: ActionContext): HistoryActions {
  return {
    undo(): void {
      ctx.history.undo();
    },

    redo(): void {
      ctx.history.redo();
    },

    clearHistory(): void {
      ctx.history.clear();
    },
  };
}
